/**
 * ScorecardColumnHighlight - Alpine.js Data Component
 * Manages visual column highlighting with multi-select support (Ctrl/Cmd+Click)
 * 
 * All functionality is namespaced under 'columnHighlight' to avoid key collisions
 * with parent components.
 */
export default (config = {}) => ({
    // Namespace all column highlight functionality
    columnHighlight: {
        scorecard: config.scorecard || {},
        theme: config.theme || '',
        activeHighlights: {},
        resizeObserver: null,
        toggleHandler: null,
        
        getColorFromTheme() {
            const themeShape = this.theme;
            const colorMap = {
                'Muted Lagoon': '#A855F7',
                'Rosy Verdant': '#0EA5E9',
                'Mauve Meadow': '#F97316',
                'Amber Twilight': '#10B981',
                'Crimson Azure': '#22C55E',
                'Ember Ocean': '#22C55E',
                'Future Ready CFO': '#DD852D',
                'Future Ready CFO High Contrast': '#DD852D',
                'NSQ Dark': '#5FB7D4'
            };

            return colorMap[themeShape] || '#0EA5E9';
        },
        
        onClick(columnIndex, event, $refs, $nextTick) {
            columnIndex = parseInt(columnIndex, 10);
            const isMultiSelect = event && (event.ctrlKey || event.metaKey);
            
            if (!isMultiSelect) {
                this.removeAllExcept(columnIndex);
            }
            
            if (this.activeHighlights[columnIndex]) {
                this.remove(columnIndex);
            } else {
                this.add(columnIndex, $refs);
            }
        },
        
        add(columnIndex, $refs) {
            columnIndex = parseInt(columnIndex);
            
            const headerContainer = $refs.headerScroll || document.getElementById('scorecard-view-scroll-container');
            const dataContainer = $refs.rightScroll || document.getElementById('scorecard-view-scroll-container');
            
            if (!headerContainer || !dataContainer) return;
            
            const headerCells = headerContainer.querySelectorAll(`[data-col-idx="${columnIndex}"]`);
            const dataCells = dataContainer.querySelectorAll(`[data-col-idx="${columnIndex}"]`);
            
            if (headerCells.length === 0 && dataCells.length === 0) return;
            
            const highlightElements = [];
            const referenceCell = headerCells[0] || dataCells[0];
            const cellRect = referenceCell.getBoundingClientRect();
            const cellWidth = cellRect.width;
            
            if (headerCells.length > 0) {
                const headerHighlightElements = this.createOverlay({
                    container: headerContainer,
                    cellRect: cellRect,
                    cellWidth: cellWidth,
                    wrapperSelector: '[data-highlight-header-wrapper]'
                });
                highlightElements.push(...headerHighlightElements);
            }
            
            if (dataCells.length > 0) {
                const dataHighlightElements = this.createOverlay({
                    container: dataContainer,
                    cellRect: cellRect,
                    cellWidth: cellWidth,
                    wrapperSelector: '[data-highlight-wrapper]',
                    useBottomPosition: true
                });
                highlightElements.push(...dataHighlightElements);
            }
            
            if (highlightElements.length > 0) {
                this.activeHighlights[columnIndex] = highlightElements;
            }
        },
        
        createOverlay({ container, cellRect, cellWidth, wrapperSelector, useBottomPosition = false }) {
            const containerRect = container.getBoundingClientRect();
            const scrollLeft = container.scrollLeft;
            const leftPosition = cellRect.left - containerRect.left + scrollLeft;
            const elements = [];
            
            // Background highlight
            const highlight = document.createElement('div');
            highlight.style.position = 'absolute';
            highlight.style.top = '0px';
            highlight.style.left = `${leftPosition}px`;
            highlight.style.width = `${cellWidth}px`;
            highlight.style.pointerEvents = 'none';
            highlight.style.zIndex = '5';
            highlight.style.backgroundColor = this.getColorFromTheme();
            highlight.classList.add('opacity-10');
            
            // Border highlight
            const border = document.createElement('div');
            border.style.position = 'absolute';
            border.style.top = '0px';
            border.style.left = `${leftPosition - 1}px`;
            border.style.width = `${cellWidth}px`;
            border.style.zIndex = '5';
            border.style.pointerEvents = 'none';
            border.style.borderColor = this.getColorFromTheme();
            border.classList.add('border-solid', 'border-2');
            
            const wrapper = container.querySelector(wrapperSelector);
            if (wrapper) {
                const computedStyle = window.getComputedStyle(wrapper);
                if (computedStyle.position === 'static') {
                    wrapper.style.position = 'relative';
                }
                
                if (useBottomPosition) {
                    // Use scrollHeight to ensure it covers the full scrollable area
                    const height = `${wrapper.scrollHeight}px`;
                    highlight.style.height = height;
                    border.style.height = height;
                } else {
                    highlight.style.height = '100%';
                    border.style.height = '100%';
                }

                wrapper.appendChild(highlight);
                wrapper.appendChild(border);
                elements.push(highlight, border);
            }
            
            return elements;
        },
        
        remove(columnIndex) {
            if (!this.activeHighlights[columnIndex]) return;
            
            this.activeHighlights[columnIndex].forEach(el => el.remove());
            delete this.activeHighlights[columnIndex];
        },
        
        removeAll() {
            Object.keys(this.activeHighlights).forEach(idx => {
                this.remove(idx);
            });
        },
        
        removeAllExcept(columnIndex) {
            Object.keys(this.activeHighlights).forEach(idx => {
                if (String(columnIndex) !== idx) {
                    this.remove(idx);
                }
            });
        },
        
        rebuild($refs, $nextTick) {
            if (!Object.keys(this.activeHighlights).length) return;
            
            const indices = Object.keys(this.activeHighlights);
            indices.forEach(idx => {
                this.remove(idx);
                $nextTick(() => {
                    this.add(idx, $refs);
                });
            });
        },

        reposition($refs) {
            if (!Object.keys(this.activeHighlights).length) return;
            
            const indices = Object.keys(this.activeHighlights);
            indices.forEach(idx => {
                this.remove(idx);
                this.add(idx, $refs);
            });
        },
        
        initResizeObserver($refs, $nextTick) {
            const headerContainer = $refs.headerScroll || document.getElementById('scorecard-view-scroll-container');
            const dataContainer = $refs.rightScroll || document.getElementById('scorecard-view-scroll-container');
            
            if (!headerContainer || !dataContainer) return;
            
            let debounceTimer;
            this.resizeObserver = new ResizeObserver(() => {
                if (debounceTimer) clearTimeout(debounceTimer);
                debounceTimer = setTimeout(() => {
                    this.rebuild($refs, $nextTick);
                }, 100);
            });

            this.resizeObserver.observe(headerContainer);
            this.resizeObserver.observe(dataContainer);

            // Observe the content wrapper to detect height changes (e.g. section collapse)
            const contentWrapper = dataContainer.querySelector('[data-highlight-wrapper]');
            if (contentWrapper) {
                this.resizeObserver.observe(contentWrapper);
            }
        },
        
        cleanup() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }
            if (this.toggleHandler) {
                window.removeEventListener('scorecard-section-toggled', this.toggleHandler);
            }
            this.removeAll();
        },
        
        // Public API - called by parent component
        init($refs, $nextTick) {
            this.initResizeObserver($refs, $nextTick);

            this.toggleHandler = () => {
                $nextTick(() => {
                    this.rebuild($refs, $nextTick);
                    // Safety check for any transitions
                    setTimeout(() => {
                        this.rebuild($refs, $nextTick);
                    }, 50);
                });
            };
            
            window.addEventListener('scorecard-section-toggled', this.toggleHandler);
        },
        
        destroy() {
            this.cleanup();
        }
    },
    
    // Convenience methods for template usage
    onColumnHeaderClick(columnIndex, event) {
        this.columnHighlight.onClick(columnIndex, event, this.$refs, this.$nextTick);
    },
    
    initColumnHighlightFeature() {
        this.columnHighlight.init(this.$refs, this.$nextTick);
    },
    
    destroyColumnHighlightFeature() {
        this.columnHighlight.destroy();
    }
});
